import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useForm } from "react-hook-form";
import { X, User, FileText, Camera, Upload, Trash2, Scan } from "lucide-react";
import {
  Customer,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  CreateCustomerFormData,
} from "../../types/customer";
import { Country } from "../../api/services/countries/type";

// Extended types for camera capabilities
interface ExtendedMediaTrackCapabilities extends MediaTrackCapabilities {
  focusMode?: string[];
  zoom?: {
    min?: number;
    max?: number;
  };
  pointsOfInterest?: boolean;
}
import CustomInput from "../ui/CustomInput";
import PrimaryButton from "../ui/PrimaryButton";
import { ReactComponent as MailIcon } from "../../assets/ic_mail.svg";
import { ReactComponent as UserIcon } from "../../assets/ic_user.svg";
import { ReactComponent as GlobeIcon } from "../../assets/ic_globe.svg";
import { ReactComponent as CalendarIcon } from "../../assets/ic_calendar.svg";
import { ReactComponent as PassportIcon } from "../../assets/ic_passport.svg";
import { ReactComponent as AttachmentIcon } from "../../assets/ic_attachment.svg";
import { ReactComponent as ArrowDown } from "../../assets/ic_arrow_down.svg";
import { ReactComponent as CameraIcon } from "../../assets/ic_camera.svg";
import { ReactComponent as PhoneIcon } from "../../assets/ic_phone.svg";
import { ReactComponent as UserBoxIcon } from "../../assets/ic_user_box.svg";
import { ReactComponent as ArrowLeft } from "../../assets/ic_arrow_left.svg";
import { ReactComponent as ArrowRight } from "../../assets/ic_arrow_right.svg";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import {
  useCreateCustomerWithFiles,
  useUpdateCustomer,
  useUpdateCustomerPhoto,
  useUpdateDocument,
  useCustomerDocuments,
} from "../../hooks/useCustomers";
import { useStatesByCountry } from "../../hooks/useState";
import { PassportScanner } from "../passportReader/PassportScanner";
import { PassportData } from "../../services/passportReader/passportReaderService";
import {
  dataURLtoFile,
  getBestDocumentImage,
  getBestHeadPhoto,
  isValidDataURL,
} from "../../utils/imageUtils";

interface CustomerFormProps {
  customer?: Customer;
  countries: Country[];
  isLoading?: boolean;
  onSubmit: (data: CreateCustomerRequest | UpdateCustomerRequest) => void;
  onCancel: () => void;
  onCustomerCreated?: (customer: Customer) => void;
  title?: string;
}

interface CustomerFormData {
  name: string;
  email?: string;
  phone?: string;
  playerId?: string;
  customerType: "regular" | "vip" | "vvip";
  countryId: string;
  stateId?: string;
  royalty: number;
  assignPlayerId: boolean;
  dateOfBirth?: string;

  requiredDocsType?: string;
  requiredDocsExpiresAt?: string;
  requiredDocs?: File;
  requiredDocsNumber?: string;
  optionalDocsType?: string;
  optionalDocsExpiresAt?: string;
  optionalDocs?: File;
  optionalDocsNumber?: string;
  customerCardNumber?: string | null;

  photo?: File;
}

const DOCUMENT_TYPES = [
  { value: "passport", label: "Passport" },
  { value: "license", label: "License" },
  { value: "aadhar_card", label: "Aadhar Card" },
  { value: "voter_card", label: "Voter Card" },
  { value: "green_book", label: "Green Book" },
  { value: "refugee_card", label: "Refugee Card" },
];

const DOCUMENT_TYPES_WITH_EXPIRY = ["passport", "license"];

export const CustomerForm: React.FC<CustomerFormProps> = ({
  customer,
  countries,
  isLoading = false,
  onCancel,
  title,
  onCustomerCreated,
}) => {
  // Start at step 0 (scanner) for new customers, step 1 for editing
  const [currentStep, setCurrentStep] = useState(customer ? 1 : 0);
  const [requiredDocPreview, setRequiredDocPreview] = useState<string | null>(
    null,
  );
  const [optionalDocPreview, setOptionalDocPreview] = useState<string | null>(
    null,
  );
  const [isRequiredDocFromScanner, setIsRequiredDocFromScanner] =
    useState(false);
  const [, setPhotoPreview] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [optionalDocError, setOptionalDocError] = useState<string>("");
  const [scanningDocument, setScanningDocument] = useState<
    "required" | "optional" | null
  >(null);
  const [documentCameraActive, setDocumentCameraActive] = useState(false);
  const [capturedDocumentPreview, setCapturedDocumentPreview] = useState<
    string | null
  >(null);
  const [cameraZoom, setCameraZoom] = useState(1);
  const [supportsZoom, setSupportsZoom] = useState(false);
  const [supportsFocus, setSupportsFocus] = useState(false);
  const documentVideoRef = React.useRef<HTMLVideoElement>(null);
  const documentCanvasRef = React.useRef<HTMLCanvasElement>(null);
  const documentStreamRef = React.useRef<MediaStream | null>(null);

  const updateCustomerMutation = useUpdateCustomer();
  const updatePhotoMutation = useUpdateCustomerPhoto();
  const updateDocumentMutation = useUpdateDocument();
  const { data: customerDocuments } = useCustomerDocuments(customer?.id || "");

  const createCustomerMutation = useCreateCustomerWithFiles();

  const isEditMode = !!customer;
  const formTitle =
    title || (isEditMode ? "Edit Customer" : "Create New Player Account");

  const handlePassportScanSuccess = (data: PassportData) => {
    console.log("========== RAW SCAN DATA RECEIVED ==========");
    console.log("Complete PassportData object:", data);
    console.log("==========================================");

    try {
      // Fill in the form fields with passport data
      if (data.fullName || data.firstName) {
        setValue(
          "name",
          data.fullName || `${data.firstName} ${data.lastName}`.trim(),
        );
      }

      // Set date of birth
      if (data.dateOfBirth) {
        setValue("dateOfBirth", data.dateOfBirth);
      }

      // Set document type (auto-detected or default to passport)
      const docType = data.documentType || "passport";
      setValue("requiredDocsType", docType);

      // Set document number
      if (data.documentNumber) {
        setValue("requiredDocsNumber", data.documentNumber);
      }

      // Set expiry date if available
      if (data.expiryDate) {
        setValue("requiredDocsExpiresAt", data.expiryDate);
      }

      // Find and set the country based on nationality or issuing country
      // First try to map ISO country code to country name
      const countryCodeToName = (code: string): string | null => {
        const codeMap: Record<string, string> = {
          NPL: "Nepal",
          IND: "India",
          USA: "United States",
          GBR: "United Kingdom",
          CHN: "China",
          JPN: "Japan",
          KOR: "South Korea",
          DEU: "Germany",
          FRA: "France",
          ITA: "Italy",
          ESP: "Spain",
          CAN: "Canada",
          AUS: "Australia",
          NZL: "New Zealand",
          PAK: "Pakistan",
          BGD: "Bangladesh",
          LKA: "Sri Lanka",
          THA: "Thailand",
          MYS: "Malaysia",
          SGP: "Singapore",
          IDN: "Indonesia",
          PHL: "Philippines",
          VNM: "Vietnam",
          ARE: "United Arab Emirates",
          SAU: "Saudi Arabia",
          QAT: "Qatar",
          KWT: "Kuwait",
          OMN: "Oman",
          BHR: "Bahrain",
          RUS: "Russia",
          BRA: "Brazil",
          MEX: "Mexico",
          ARG: "Argentina",
          CHL: "Chile",
          ZAF: "South Africa",
          EGY: "Egypt",
          NGA: "Nigeria",
          KEN: "Kenya",
          ETH: "Ethiopia",
        };
        return codeMap[code.toUpperCase()] || null;
      };

      // Try to find country by code first, then by name
      let country = null;

      // Try nationality code
      if (data.nationality) {
        const countryName = countryCodeToName(data.nationality);
        if (countryName) {
          country = countries.find(
            (c) => c.name.toLowerCase() === countryName.toLowerCase(),
          );
        }
        if (!country) {
          // Try exact match with nationality
          country = countries.find(
            (c) => c.name.toLowerCase() === data.nationality?.toLowerCase(),
          );
        }
      }

      // Try issuing country if nationality didn't work
      if (!country && data.issuingCountry) {
        const countryName = countryCodeToName(data.issuingCountry);
        if (countryName) {
          country = countries.find(
            (c) => c.name.toLowerCase() === countryName.toLowerCase(),
          );
        }
        if (!country) {
          // Try exact match with issuing country
          country = countries.find(
            (c) => c.name.toLowerCase() === data.issuingCountry?.toLowerCase(),
          );
        }
      }

      if (country) {
        setValue("countryId", country.id);
        console.log("✓ Country matched:", country.name);
      } else {
        console.log("⚠ Could not find country for:", {
          nationality: data.nationality,
          issuingCountry: data.issuingCountry,
        });
      }

      // Handle document image - use the best quality image available
      const documentImageUrl = getBestDocumentImage({
        whiteImage: data.whiteImage,
        irImage: data.irImage,
        uvImage: data.uvImage,
        fullDocumentImage: data.fullDocumentImage,
      });

      let hasDocumentImage = false;
      let hasCustomerPhoto = false;

      if (documentImageUrl && isValidDataURL(documentImageUrl)) {
        try {
          const docFile = dataURLtoFile(
            documentImageUrl,
            `document_${docType}.jpg`,
          );
          setValue("requiredDocs", docFile);
          setRequiredDocPreview(documentImageUrl);
          setIsRequiredDocFromScanner(true); // Mark as from scanner
          hasDocumentImage = true;
          console.log("✓ Document image captured from scanner");
        } catch (error) {
          console.error("Failed to convert document image:", error);
          toast.error("Failed to process document image");
        }
      } else {
        console.log("⚠ No document image from scanner");
        setIsRequiredDocFromScanner(false);
      }

      // Handle head photo - use the best quality photo available
      const headPhotoUrl = getBestHeadPhoto({
        chipHeadImage: data.chipHeadImage,
        ocrHeadImage: data.ocrHeadImage,
        sidHeadImage: data.sidHeadImage,
        photo: data.photo,
      });

      if (headPhotoUrl && isValidDataURL(headPhotoUrl)) {
        try {
          const photoFile = dataURLtoFile(headPhotoUrl, "customer_photo.jpg");
          setValue("photo", photoFile);
          setPhotoPreview(headPhotoUrl);
          hasCustomerPhoto = true;
          console.log("✓ Customer photo captured from scanner");
        } catch (error) {
          console.error("Failed to convert head photo:", error);
          toast.error("Failed to process customer photo");
        }
      } else {
        console.log("⚠ No customer photo from scanner");
      }

      // Show appropriate success message
      if (hasDocumentImage && hasCustomerPhoto) {
        toast.success("Document and photo captured successfully!");
      } else if (hasDocumentImage) {
        toast.success("Document captured! Please add customer photo manually.");
      } else if (hasCustomerPhoto) {
        toast.success("Photo captured! Please upload document manually.");
      } else {
        toast.error(
          "Data imported. Please upload document and photo manually.",
        );
      }

      // Log all received data for debugging
      console.log("Passport scan data received:", {
        personalInfo: {
          name: data.fullName,
          dob: data.dateOfBirth,
          nationality: data.nationality,
        },
        document: {
          type: docType,
          number: data.documentNumber,
          expiry: data.expiryDate,
        },
        images: {
          hasDocumentImage: !!documentImageUrl,
          hasHeadPhoto: !!headPhotoUrl,
          whiteImage: !!data.whiteImage,
          irImage: !!data.irImage,
          uvImage: !!data.uvImage,
        },
      });

      toast.success(
        `${docType === "passport" ? "Passport" : "Document"} data imported successfully!`,
      );
      // Move to step 1 after successful scan
      setCurrentStep(1);
    } catch (error) {
      console.error("Error processing passport scan data:", error);
      toast.error("Failed to process scanned data. Please try again.");
    }
  };

  const handlePassportScanError = (error: string) => {
    toast.error(`Passport scan failed: ${error}`);
  };

  const formatDateForInput = (dateString?: string) => {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  // Calculate default date of birth (25 years ago from today)
  const getDefaultDateOfBirth = (): string => {
    const today = new Date();
    const defaultDate = new Date(
      today.getFullYear() - 25,
      today.getMonth(),
      today.getDate(),
    );
    return formatDateForInput(defaultDate.toISOString());
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    trigger,
    getValues,
    watch,
    reset,
    clearErrors,
  } = useForm<CustomerFormData>({
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      playerId: "",
      customerType: "regular",
      countryId: "",
      stateId: "",
      royalty: 0,
      assignPlayerId: false,
      dateOfBirth: "",
      requiredDocsType: "",
      requiredDocsExpiresAt: "",
      requiredDocsNumber: "",
      optionalDocsType: "",
      optionalDocsExpiresAt: "",
      optionalDocsNumber: "",
      customerCardNumber: null,
    },
  });

  const watchAssignPlayerId = watch("assignPlayerId");
  const watchCountryId = watch("countryId");
  const watchRequiredDocsType = watch("requiredDocsType");
  const watchOptionalDocsType = watch("optionalDocsType");

  const { data: statesData } = useStatesByCountry(watchCountryId);

  const states = useMemo(() => statesData || [], [statesData]);

  React.useEffect(() => {
    if (!watchAssignPlayerId) {
      setValue("playerId", "");
    }
  }, [watchAssignPlayerId, setValue]);

  React.useEffect(() => {
    if (customer && customer.countryId === watchCountryId) {
      return;
    }
    setValue("stateId", "");
  }, [watchCountryId, setValue, customer]);

  React.useEffect(() => {
    if (customer) {
      reset({
        name: customer.name,
        email: customer.email,
        phone: customer.phone,
        playerId: customer.playerId,
        customerType: customer.customerType,
        countryId: customer.countryId,
        stateId: customer.stateId || "",
        royalty: customer.royalty,
        assignPlayerId: false,
        dateOfBirth: formatDateForInput(customer.dateOfBirth),
        requiredDocsType: "",
        requiredDocsExpiresAt: "",
        requiredDocsNumber: "",
        optionalDocsType: "",
        optionalDocsExpiresAt: "",
        optionalDocsNumber: "",
        customerCardNumber: customer.customerCardNumber || null,
        photo: undefined,
        requiredDocs: undefined,
        optionalDocs: undefined,
      });
    }
  }, [customer, reset]);

  React.useEffect(() => {
    if (customer && customerDocuments && customerDocuments.length > 0) {
      const requiredDoc = customerDocuments.find((doc) => doc.isRequired);
      const optionalDoc = customerDocuments.find((doc) => !doc.isRequired);

      if (requiredDoc) {
        setValue("requiredDocsType", requiredDoc.docsType);
        setValue("requiredDocsNumber", requiredDoc.documentNumber || "");
        setValue(
          "requiredDocsExpiresAt",
          requiredDoc.expireDate
            ? formatDateForInput(requiredDoc.expireDate)
            : "",
        );
        if (requiredDoc.filePath) {
          setRequiredDocPreview(
            `${process.env.REACT_APP_API_BASE_URL}/${requiredDoc.filePath}`,
          );
        }
      }

      if (optionalDoc) {
        setValue("optionalDocsType", optionalDoc.docsType);
        setValue("optionalDocsNumber", optionalDoc.documentNumber || "");
        setValue(
          "optionalDocsExpiresAt",
          optionalDoc.expireDate
            ? formatDateForInput(optionalDoc.expireDate)
            : "",
        );
        if (optionalDoc.filePath) {
          setOptionalDocPreview(
            `${process.env.REACT_APP_API_BASE_URL}/${optionalDoc.filePath}`,
          );
        }
      }
    }
  }, [customer, customerDocuments, setValue]);

  React.useEffect(() => {
    if (customer && customer.stateId && states.length > 0) {
      const customerState = states.find(
        (state) => state.id === customer.stateId,
      );
      if (customerState) {
        setValue("stateId", customer.stateId);
      }
    }
  }, [customer, states, setValue]);

  const steps = [
    { number: 1, title: "Basic Details", icon: User },
    { number: 2, title: "Documents", icon: FileText },
    { number: 3, title: "Photo (Optional)", icon: Camera },
  ];

  const handleStepClick = async (stepNumber: number) => {
    if (!isEditMode) return;

    if (stepNumber === currentStep) return;

    if (stepNumber === 3) {
      resetFormToCustomerData();
    }

    setCurrentStep(stepNumber);
  };

  const clearStepValidationErrors = () => {
    clearErrors();
  };

  const validateOptionalDocument = () => {
    const formData = getValues();
    const hasOptionalDoc = !!formData.optionalDocs;
    const hasOptionalDocType = !!formData.optionalDocsType;
    const hasOptionalDocNumber = !!formData.optionalDocsNumber;

    if (hasOptionalDoc && (!hasOptionalDocType || !hasOptionalDocNumber)) {
      if (!hasOptionalDocType && !hasOptionalDocNumber) {
        setOptionalDocError(
          "Please select document type and enter document number for the uploaded document.",
        );
      } else if (!hasOptionalDocType) {
        setOptionalDocError(
          "Please select document type for the uploaded document.",
        );
      } else if (!hasOptionalDocNumber) {
        setOptionalDocError(
          "Please enter document number for the uploaded document.",
        );
      }
      return false;
    }

    if ((hasOptionalDocType || hasOptionalDocNumber) && !hasOptionalDoc) {
      setOptionalDocError(
        "Please upload the document or clear the document details.",
      );
      return false;
    }

    setOptionalDocError("");
    return true;
  };

  const handleNextStep = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();

    let fieldsToValidate: (keyof CustomerFormData)[] = [];

    if (currentStep === 1) {
      fieldsToValidate = [
        "name",
        "countryId",
        "phone",
        "dateOfBirth",
        "customerCardNumber",
      ];

      if (states.length > 0) {
        fieldsToValidate.push("stateId");
      }

      const isValid = await trigger(fieldsToValidate);
      if (!isValid) return;

      if (isEditMode && customer) {
        const formData = getValues();
        const updateData: UpdateCustomerRequest = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          royalty: formData.royalty,
          customerType: formData.customerType,
          countryId: formData.countryId,
          stateId: formData.stateId || undefined,
          customerCardNumber: formData.customerCardNumber || null,
          dateOfBirth: formData.dateOfBirth,
        };

        try {
          await updateCustomerMutation.mutateAsync({
            customerId: customer.id,
            data: updateData,
          });
          toast.success("Basic details updated successfully!");
        } catch {
          toast.error("Failed to update customer basic info");
          return;
        }
      }
    } else if (currentStep === 2) {
      if (!validateOptionalDocument()) {
        return;
      }

      if (isEditMode) {
        const formData = getValues();
        if (customerDocuments && customerDocuments.length > 0) {
          const requiredDoc = customerDocuments.find((doc) => doc.isRequired);
          const optionalDoc = customerDocuments.find((doc) => !doc.isRequired);

          if (
            requiredDoc &&
            (formData.requiredDocs ||
              formData.requiredDocsType ||
              formData.requiredDocsExpiresAt ||
              formData.requiredDocsNumber)
          ) {
            if (formData.requiredDocsExpiresAt) {
              const docType = formData.requiredDocsType || requiredDoc.docsType;
              if (DOCUMENT_TYPES_WITH_EXPIRY.includes(docType)) {
                const selected = new Date(formData.requiredDocsExpiresAt);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                if (selected < today) {
                  toast.error(
                    "Required document expiry date cannot be in the past",
                  );
                  return;
                }
              }
            }

            const updateData = {
              docsType: formData.requiredDocsType,
              documentNumber: formData.requiredDocsNumber,
              expireDate: formData.requiredDocsExpiresAt,
              file: formData.requiredDocs,
            };

            try {
              await updateDocumentMutation.mutateAsync({
                documentId: requiredDoc.id,
                data: updateData,
              });
            } catch {
              toast.error("Failed to update required document");
              return;
            }
          }

          if (
            optionalDoc &&
            (formData.optionalDocs ||
              formData.optionalDocsType ||
              formData.optionalDocsExpiresAt ||
              formData.optionalDocsNumber)
          ) {
            if (formData.optionalDocsExpiresAt) {
              const docType = formData.optionalDocsType || optionalDoc.docsType;
              if (DOCUMENT_TYPES_WITH_EXPIRY.includes(docType)) {
                const selected = new Date(formData.optionalDocsExpiresAt);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                if (selected < today) {
                  toast.error(
                    "Optional document expiry date cannot be in the past",
                  );
                  return;
                }
              }
            }

            const updateData = {
              docsType: formData.optionalDocsType,
              documentNumber: formData.optionalDocsNumber,
              expireDate: formData.optionalDocsExpiresAt,
              file: formData.optionalDocs,
            };

            try {
              await updateDocumentMutation.mutateAsync({
                documentId: optionalDoc.id,
                data: updateData,
              });
            } catch {
              toast.error("Failed to update optional document");
              return;
            }
          }
        }
      } else {
        fieldsToValidate = ["requiredDocsType", "requiredDocsNumber"];

        const requiredDocType = getValues("requiredDocsType");
        if (
          requiredDocType &&
          DOCUMENT_TYPES_WITH_EXPIRY.includes(requiredDocType)
        ) {
          fieldsToValidate.push("requiredDocsExpiresAt");
        }

        const optionalDocType = getValues("optionalDocsType");
        if (
          optionalDocType &&
          DOCUMENT_TYPES_WITH_EXPIRY.includes(optionalDocType)
        ) {
          fieldsToValidate.push("optionalDocsExpiresAt");
        }

        if (!getValues("requiredDocs")) {
          toast.error("Please upload a required document");
          return;
        }

        const isValid = await trigger(fieldsToValidate);
        if (!isValid) return;
      }
    }

    if (currentStep < 3) {
      clearStepValidationErrors();
      setCurrentStep(currentStep + 1);
      if (isEditMode && customer && currentStep + 1 === 3) {
        resetFormToCustomerData();
      }
    }
  };

  const resetFormToCustomerData = () => {
    if (customer) {
      reset({
        name: customer.name,
        email: customer.email,
        phone: customer.phone,
        playerId: customer.playerId,
        customerType: customer.customerType,
        countryId: customer.countryId,
        stateId: customer.stateId || "",
        royalty: customer.royalty,
        assignPlayerId: false,
        dateOfBirth: formatDateForInput(customer.dateOfBirth),
        requiredDocsType: "",
        requiredDocsExpiresAt: "",
        requiredDocsNumber: "",
        optionalDocsType: "",
        optionalDocsExpiresAt: "",
        optionalDocsNumber: "",
        customerCardNumber: customer.customerCardNumber || null,
        photo: undefined,
        requiredDocs: undefined,
        optionalDocs: undefined,
      });
    }
  };

  const handleStep3Submit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isEditMode && customer) {
      const formData = getValues();
      let photoFile = formData.photo;
      if (photoDataUrl && !photoFile) {
        photoFile = dataURLtoFile(photoDataUrl, `photo_${Date.now()}.png`);
      }

      if (photoFile) {
        try {
          await updatePhotoMutation.mutateAsync({
            customerId: customer.id,
            photoFile,
          });
          toast.success("Photo updated successfully!");
        } catch {
          toast.error("Failed to update photo");
          return;
        }
      } else {
        toast.success("No photo changes to save!");
      }

      onCancel();
    } else {
      handleSubmit(onFormSubmit)(e);
    }
  };

  const handleCancelClick = () => {
    stopCamera();
    setShowCancelDialog(true);
  };

  const handleConfirmCancel = () => {
    stopCamera();
    setShowCancelDialog(false);
    onCancel();
  };

  const handleCancelDialog = () => {
    stopCamera();
    setShowCancelDialog(false);
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      stopCamera();
      clearStepValidationErrors();
      setCurrentStep(currentStep - 1);
    }
  };

  React.useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const handleRequiredDocUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = [
        "application/pdf",
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
        "image/gif",
        "image/bmp",
        "image/svg+xml",
      ];
      if (!validTypes.includes(file.type)) {
        toast.error("Only PDF or image files are allowed for document upload.");
        e.target.value = "";
        return;
      }
      setValue("requiredDocs", file);
      setIsRequiredDocFromScanner(false); // Mark as manual upload
      const reader = new FileReader();
      reader.onload = (e) => {
        setRequiredDocPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOptionalDocUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = [
        "application/pdf",
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
        "image/gif",
        "image/bmp",
        "image/svg+xml",
      ];
      if (!validTypes.includes(file.type)) {
        toast.error("Only PDF or image files are allowed for document upload.");
        e.target.value = "";
        return;
      }
      setValue("optionalDocs", file);
      setOptionalDocError(""); // Clear error when file is uploaded
      const reader = new FileReader();
      reader.onload = (e) => {
        setOptionalDocPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const startDocumentCamera = async (docType: "required" | "optional") => {
    try {
      setScanningDocument(docType);
      setDocumentCameraActive(true);
      setCapturedDocumentPreview(null);
      setCameraZoom(1);
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        // First, get basic stream to check capabilities
        const basicStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });

        const track = basicStream.getVideoTracks()[0];
        const capabilities =
          track.getCapabilities() as ExtendedMediaTrackCapabilities;

        console.log("Camera capabilities:", capabilities);

        // Check for zoom support
        if (capabilities.zoom && typeof capabilities.zoom === "object") {
          setSupportsZoom(true);
        }

        // Check for focus support
        if (capabilities.focusMode && capabilities.focusMode.length > 0) {
          setSupportsFocus(true);
        }

        // Build constraints based on supported capabilities
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const constraints: any = {
          facingMode: "environment",
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        };

        // Add focus mode if supported
        if (capabilities.focusMode) {
          if (capabilities.focusMode.includes("continuous")) {
            constraints.focusMode = "continuous";
          } else if (capabilities.focusMode.includes("auto")) {
            constraints.focusMode = "auto";
          }
          console.log("Focus modes available:", capabilities.focusMode);
        }

        // Stop the basic stream
        basicStream.getTracks().forEach((t) => t.stop());

        // Get stream with proper constraints
        const stream = await navigator.mediaDevices.getUserMedia({
          video: constraints,
        });

        documentStreamRef.current = stream;

        if (documentVideoRef.current) {
          documentVideoRef.current.srcObject = stream;
          await documentVideoRef.current.play();

          // Log final track settings
          const finalTrack = stream.getVideoTracks()[0];
          const settings = finalTrack.getSettings();
          console.log("Applied camera settings:", settings);
        }
      }
    } catch (error) {
      console.error("Camera access error:", error);
      toast.error("Unable to access camera. Please check permissions.");
      setDocumentCameraActive(false);
      setScanningDocument(null);
    }
  };

  const stopDocumentCamera = () => {
    setDocumentCameraActive(false);
    setCapturedDocumentPreview(null);
    setCameraZoom(1);
    setSupportsZoom(false);
    setSupportsFocus(false);
    if (documentStreamRef.current) {
      documentStreamRef.current.getTracks().forEach((track) => track.stop());
      documentStreamRef.current = null;
    }
    if (documentVideoRef.current) {
      documentVideoRef.current.srcObject = null;
    }
    setScanningDocument(null);
  };

  const captureDocument = () => {
    if (documentVideoRef.current && documentCanvasRef.current) {
      const context = documentCanvasRef.current.getContext("2d");
      if (context) {
        documentCanvasRef.current.width = documentVideoRef.current.videoWidth;
        documentCanvasRef.current.height = documentVideoRef.current.videoHeight;
        context.drawImage(documentVideoRef.current, 0, 0);
        const dataUrl = documentCanvasRef.current.toDataURL("image/jpeg");
        setCapturedDocumentPreview(dataUrl);
      }
    }
  };

  const proceedWithCapturedDocument = () => {
    if (capturedDocumentPreview) {
      // Convert dataURL to file
      const arr = capturedDocumentPreview.split(",");
      const mime = arr[0].match(/:(.*?);/)?.[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      const file = new File([u8arr], `document_${Date.now()}.jpg`, {
        type: mime || "image/jpeg",
      });

      if (scanningDocument === "required") {
        setValue("requiredDocs", file);
        setRequiredDocPreview(capturedDocumentPreview);
        toast.success("Required document captured successfully!");
      } else if (scanningDocument === "optional") {
        setValue("optionalDocs", file);
        setOptionalDocError("");
        setOptionalDocPreview(capturedDocumentPreview);
        toast.success("Optional document captured successfully!");
      }

      stopDocumentCamera();
    }
  };

  const retakeDocument = () => {
    setCapturedDocumentPreview(null);
  };

  const handleZoomChange = async (newZoom: number) => {
    setCameraZoom(newZoom);
    if (documentStreamRef.current) {
      const track = documentStreamRef.current.getVideoTracks()[0];
      const capabilities =
        track.getCapabilities() as ExtendedMediaTrackCapabilities;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const settings = track.getSettings() as any;

      console.log("Zoom capabilities:", capabilities.zoom);
      console.log("Current settings:", settings);

      if ("zoom" in capabilities && capabilities.zoom) {
        const minZoom = capabilities.zoom.min || 1;
        const maxZoom = capabilities.zoom.max || 1;
        const zoomValue = minZoom + (maxZoom - minZoom) * ((newZoom - 1) / 4);

        console.log(
          `Setting zoom to ${zoomValue} (min: ${minZoom}, max: ${maxZoom})`,
        );

        try {
          // Try different constraint formats
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          await (track as any).applyConstraints({
            zoom: zoomValue,
          });

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const newSettings = track.getSettings() as any;
          console.log("New zoom settings:", newSettings.zoom);
        } catch (error) {
          console.error("Zoom error:", error);

          // Try alternative approach with advanced constraints
          try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            await (track as any).applyConstraints({
              advanced: [{ zoom: zoomValue }],
            });
          } catch (error2) {
            console.error("Advanced zoom also failed:", error2);
            toast.error("Zoom not supported on this camera");
          }
        }
      } else {
        console.log("Zoom not available in capabilities");
        toast.error("Your camera does not support zoom");
      }
    }
  };

  const handleFocus = async () => {
    if (documentStreamRef.current && documentVideoRef.current) {
      const track = documentStreamRef.current.getVideoTracks()[0];
      const capabilities =
        track.getCapabilities() as ExtendedMediaTrackCapabilities;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const settings = track.getSettings() as any;

      console.log("Focus capabilities:", capabilities.focusMode);
      console.log("Current focus settings:", settings.focusMode);

      // Check if we have focus capabilities
      if (capabilities.focusMode && capabilities.focusMode.length > 0) {
        try {
          // Try to trigger auto-focus by switching modes
          if (capabilities.focusMode.includes("single-shot")) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            await (track as any).applyConstraints({
              focusMode: "single-shot",
            });
            toast.success("Camera focusing...");

            // Switch back to continuous after a moment
            setTimeout(async () => {
              if (capabilities.focusMode?.includes("continuous")) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                await (track as any).applyConstraints({
                  focusMode: "continuous",
                });
              }
            }, 500);
          } else if (capabilities.focusMode.includes("manual")) {
            // Try manual focus
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            await (track as any).applyConstraints({
              focusMode: "manual",
            });

            // Try to set focus distance
            if ("focusDistance" in capabilities) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const focusCapabilities = (capabilities as any).focusDistance;
              const midFocus = focusCapabilities
                ? (focusCapabilities.min + focusCapabilities.max) / 2
                : 0;

              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              await (track as any).applyConstraints({
                focusDistance: midFocus,
              });
            }

            toast.success("Manual focus applied");

            // Try to switch back to auto
            setTimeout(async () => {
              if (capabilities.focusMode?.includes("continuous")) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                await (track as any).applyConstraints({
                  focusMode: "continuous",
                });
              }
            }, 1000);
          } else {
            // Just try to re-apply current mode to trigger refocus
            const currentMode = settings.focusMode || "continuous";
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            await (track as any).applyConstraints({
              focusMode: currentMode,
            });
            toast.success("Focus refreshed");
          }
        } catch (error) {
          console.error("Focus error:", error);

          // Try alternative: points of interest
          if ("pointsOfInterest" in capabilities) {
            try {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              await (track as any).applyConstraints({
                advanced: [
                  {
                    pointsOfInterest: [{ x: 0.5, y: 0.5 }],
                  },
                ],
              });
              toast.success("Focus point set");
            } catch (error2) {
              console.error("Points of interest failed:", error2);
              toast.error("Focus not supported on this camera");
            }
          } else {
            toast.error("Focus control not available");
          }
        }
      } else {
        console.log("No focus modes available");
        toast.error("Your camera does not support focus control");
      }
    }
  };

  const handleRequiredDocumentUpload = () => {
    // Check if we're on a mobile device
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      );

    if (isMobile) {
      // On mobile, use file input with capture
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.setAttribute("capture", "environment");
      input.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = (event) => {
            const result = event.target?.result as string;
            setValue("requiredDocs", file);
            setRequiredDocPreview(result);
            toast.success("Document captured successfully!");
          };
          reader.readAsDataURL(file);
        }
      };
      input.click();
    } else {
      // On desktop, use camera stream
      startDocumentCamera("required");
    }
  };

  const handleOptionalDocumentUpload = () => {
    // Check if we're on a mobile device
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      );

    if (isMobile) {
      // On mobile, use file input with capture
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.setAttribute("capture", "environment");
      input.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = (event) => {
            const result = event.target?.result as string;
            setValue("optionalDocs", file);
            setOptionalDocError("");
            setOptionalDocPreview(result);
            toast.success("Document captured successfully!");
          };
          reader.readAsDataURL(file);
        }
      };
      input.click();
    } else {
      // On desktop, use camera stream
      startDocumentCamera("optional");
    }
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
        "image/gif",
        "image/bmp",
        "image/svg+xml",
      ];
      if (!validTypes.includes(file.type)) {
        toast.error("Only image files are allowed for photo upload.");
        e.target.value = "";
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setPhotoDataUrl(event.target?.result as string);
        setPhotoPreview(event.target?.result as string);
        setValue("photo", file);
      };
      reader.readAsDataURL(file);
    }
  };

  const [cameraActive, setCameraActive] = useState(false);
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  const startCamera = async () => {
    setCameraActive(true);
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    }
  };

  const stopCamera = () => {
    setCameraActive(false);
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  const handleTakePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext("2d");
      if (context) {
        context.drawImage(videoRef.current, 0, 0, 400, 300);
        const dataUrl = canvasRef.current.toDataURL("image/png");
        setPhotoDataUrl(dataUrl);
        setPhotoPreview(dataUrl);

        stopCamera();
      }
    }
  };

  const onFormSubmit = async (data: CustomerFormData) => {
    if (!isEditMode && !validateOptionalDocument()) {
      return;
    }

    try {
      let photoFile = data.photo;
      if (photoDataUrl && !photoFile) {
        photoFile = dataURLtoFile(photoDataUrl, `photo_${Date.now()}.png`);
      }

      if (!data.requiredDocs) {
        toast.error("Please upload a required document");
        return;
      }

      const formDataWithFiles: CreateCustomerFormData = {
        name: data.name,
        email: data.email,
        phone: data.phone,
        royalty: data.royalty,
        customerType: data.customerType,
        countryId: data.countryId,
        stateId: data.stateId || undefined,
        playerId: data.assignPlayerId ? data.playerId : undefined,
        dateOfBirth: data.dateOfBirth,
        photo: photoFile,
        requiredDocs: data.requiredDocs,
        requiredDocsType: data.requiredDocsType,
        requiredDocsExpiresAt: data.requiredDocsExpiresAt,
        requiredDocsNumber: data.requiredDocsNumber,
        optionalDocs: data.optionalDocs,
        optionalDocsType: data.optionalDocsType,
        optionalDocsExpiresAt: data.optionalDocsExpiresAt,
        optionalDocsNumber: data.optionalDocsNumber,
        customerCardNumber: data.customerCardNumber || null,
      };

      try {
        const newCustomer =
          await createCustomerMutation.mutateAsync(formDataWithFiles);
        toast.success("Customer created successfully!");
        onCustomerCreated?.(newCustomer);
        onCancel();
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to create customer",
        );
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to create customer",
      );
    }
  };

  const renderStepIndicator = () => (
    <div className="flex justify-between items-center mb-1 pb-1 px-4">
      {steps.map((step) => {
        const isActive = currentStep === step.number;
        const isCompleted = currentStep > step.number;

        return (
          <React.Fragment key={step.number}>
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                  isActive
                    ? "bg-blue-900 text-white"
                    : isCompleted
                      ? "bg-green-900 text-white"
                      : "bg-slate-900 text-slate-400"
                } ${isEditMode ? "cursor-pointer hover:bg-blue-800" : ""}`}
                onClick={() => isEditMode && handleStepClick(step.number)}
              >
                <span className="text-sm font-medium">{step.number}</span>
              </div>
              <span
                className={`text-xs ${
                  isActive ? "text-white" : "text-slate-400"
                } ${isEditMode ? "cursor-pointer" : ""}`}
                onClick={() => isEditMode && handleStepClick(step.number)}
              >
                {step.title}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  const renderStep0 = () => (
    <div className="space-y-6 py-4">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">
          Scan Document to Auto-Fill Form
        </h3>
        <p className="text-slate-400 text-sm">
          Place your passport, ID card, or license on the scanner to
          automatically fill the registration form, or click &quot;Skip&quot; to
          fill manually.
        </p>
      </div>

      <PassportScanner
        onScanSuccess={handlePassportScanSuccess}
        onScanError={handlePassportScanError}
        autoConnect={true}
        showStatus={true}
      />

      <div className="flex justify-center pt-4">
        <button
          type="button"
          onClick={() => setCurrentStep(1)}
          className="px-6 py-2 text-slate-400 hover:text-white border border-slate-600 hover:border-slate-500 rounded-lg transition-colors"
        >
          Skip and Fill Manually
        </button>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-3">
      <div className="text-left mb-2">
        <p className="text-slate-400 text-sm">
          Please fill in customer&apos;s basic details to get started.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <CustomInput
          label="Full Name"
          type="text"
          placeholder="Full Name"
          register={register}
          name="name"
          validation={{
            required: "Name is required",
            validate: (value) => {
              if (typeof value !== "string" || value.trim().length === 0) {
                return "Name cannot be empty or spaces only";
              }
              return true;
            },
          }}
          error={errors.name}
          disabled={isLoading}
          icon={<UserIcon className="w-5 h-5 text-slate-400" />}
          autoFocus
        />
        <CustomInput
          label="Email Address (Optional)"
          type="email"
          placeholder="Email Address (Optional)"
          register={register}
          name="email"
          validation={{
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address",
            },
          }}
          error={errors.email}
          disabled={isLoading}
          icon={<MailIcon className="w-5 h-5 text-slate-400" />}
        />
        <CustomInput
          label="Customer Card Number"
          type="text"
          placeholder="Enter Customer Card Number"
          register={register}
          name="customerCardNumber"
          validation={{
            validate: (value) => {
              if (
                value &&
                typeof value === "string" &&
                value.trim().length === 0
              ) {
                return "Customer card number cannot be empty or spaces only";
              }
              return true;
            },
          }}
          error={errors.customerCardNumber}
          disabled={isLoading}
          icon={<UserBoxIcon className="w-5 h-5 text-slate-400" />}
        />
        <CustomInput
          label="Player Id"
          type="text"
          placeholder="Player Id"
          register={register}
          name="playerId"
          validation={{
            valueAsNumber: true,
            validate: (value) => {
              if (watchAssignPlayerId) {
                const num = typeof value === "string" ? Number(value) : value;
                if (typeof num !== "number" || isNaN(num)) {
                  return "Player Id must be a valid number";
                }
                if (!Number.isInteger(num) || num <= 0) {
                  return "Player Id must be a positive integer";
                }
              }
              return true;
            },
          }}
          error={errors.playerId}
          disabled={isLoading || isEditMode || !watchAssignPlayerId}
          icon={<UserBoxIcon className="w-5 h-5 text-slate-400" />}
          autoFocus
        />
        {!isEditMode && (
          <div className="flex items-center gap-3 col-span-2">
            <input
              type="checkbox"
              id="assignPlayerId"
              {...register("assignPlayerId")}
              className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
              disabled={isLoading}
            />
            <label htmlFor="assignPlayerId" className="text-slate-300">
              Assign Player ID Manually
            </label>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Country
          </label>
          <div className="relative flex items-center bg-slate-700 rounded-lg border border-slate-600 focus-within:ring-2 focus-within:ring-blue-500">
            <span className="absolute left-3 flex items-center pointer-events-none">
              <GlobeIcon className="w-5 h-5 text-slate-400" />
            </span>
            <select
              {...register("countryId", {
                required: "Please select a country",
              })}
              className="w-full pl-10 pr-8 py-3 bg-slate-700 text-white rounded-lg focus:outline-none appearance-none"
              disabled={isLoading}
            >
              <option value="">Country</option>
              {countries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name} ({country.callingCode})
                </option>
              ))}
            </select>
            <span className="absolute right-3 pointer-events-none flex items-center">
              <ArrowDown className="w-6 h-6 text-slate-400" />
            </span>
          </div>
          {errors.countryId && (
            <p className="mt-1 text-sm text-red-400">
              {errors.countryId.message}
            </p>
          )}
        </div>

        {/* State field - only show if states exist for selected country */}
        {states.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              State
            </label>
            <div className="relative flex items-center bg-slate-700 rounded-lg border border-slate-600 focus-within:ring-2 focus-within:ring-blue-500">
              <span className="absolute left-3 flex items-center pointer-events-none">
                <GlobeIcon className="w-5 h-5 text-slate-400" />
              </span>
              <select
                {...register("stateId", {
                  required: states.length > 0 ? "Please select a state" : false,
                })}
                className="w-full pl-10 pr-8 py-3 bg-slate-700 text-white rounded-lg focus:outline-none appearance-none"
                disabled={isLoading}
              >
                <option value="">Select State</option>
                {states.map((state) => (
                  <option key={state.id} value={state.id}>
                    {state.name}
                  </option>
                ))}
              </select>
              <span className="absolute right-3 pointer-events-none flex items-center">
                <ArrowDown className="w-6 h-6 text-slate-400" />
              </span>
            </div>
            {errors.stateId && (
              <p className="mt-1 text-sm text-red-400">
                {errors.stateId.message}
              </p>
            )}
          </div>
        )}

        <CustomInput
          label="Date of Birth"
          type="date"
          placeholder="YYYY-MM-DD or click calendar icon"
          register={register}
          name="dateOfBirth"
          validation={{
            required: "Date of birth is required",
            validate: (value) => {
              if (value) {
                const selected = new Date(value as string);
                const today = new Date();
                if (isNaN(selected.getTime())) {
                  return "Please enter a valid date";
                }
                if (selected >= today) {
                  return "Date of birth must be in the past";
                }
              }
              return true;
            },
          }}
          error={errors.dateOfBirth}
          disabled={isLoading}
          icon={<CalendarIcon className="w-5 h-5 text-slate-400" />}
          defaultCalendarDate={getDefaultDateOfBirth()}
        />

        <CustomInput
          label="Phone Number"
          type="text"
          placeholder="Phone Number"
          register={register}
          name="phone"
          validation={{
            validate: (value) => {
              if (
                typeof value === "string" &&
                value.length > 0 &&
                value.trim().length === 0
              ) {
                return "Phone number cannot be empty or spaces only";
              }
              return true;
            },
          }}
          error={errors.phone}
          disabled={isLoading}
          icon={<PhoneIcon className="w-5 h-5 text-slate-400" />}
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="text-left mb-2">
        <p className="text-slate-400 text-sm">
          Please upload documents. Required document is mandatory, optional
          document is not required.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Required Document Section */}
        <div className="border border-slate-600 rounded-lg p-3">
          <div className="mb-3">
            <h3 className="text-base font-medium text-white">
              Required Document *
            </h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Document Type
              </label>
              <div className="relative flex items-center bg-slate-700 rounded-lg border border-slate-600 focus-within:ring-2 focus-within:ring-blue-500">
                <span className="absolute left-3 flex items-center pointer-events-none">
                  <PassportIcon className="w-5 h-5 text-slate-400" />
                </span>
                <select
                  {...register("requiredDocsType", {
                    required: !isEditMode
                      ? "Please select a document type"
                      : false,
                  })}
                  className="w-full pl-10 pr-8 py-3 bg-slate-700 text-white rounded-lg focus:outline-none appearance-none"
                  disabled={isLoading}
                >
                  <option value="">Select Document Type</option>
                  {DOCUMENT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <span className="absolute right-3 pointer-events-none flex items-center">
                  <ArrowDown className="w-6 h-6 text-slate-400" />
                </span>
              </div>
              {errors.requiredDocsType && (
                <p className="mt-1 text-sm text-red-400">
                  {errors.requiredDocsType.message}
                </p>
              )}
            </div>

            <CustomInput
              label="Document Number"
              type="text"
              placeholder="Enter Document Number"
              register={register}
              name="requiredDocsNumber"
              validation={{
                required: "Document number is required for required document",
                validate: (value) => {
                  if (typeof value === "string" && value.trim().length === 0) {
                    return "Document number cannot be empty or spaces only";
                  }
                  return true;
                },
              }}
              error={errors.requiredDocsNumber}
              disabled={isLoading}
              icon={<PassportIcon className="w-5 h-5 text-slate-400" />}
            />

            {DOCUMENT_TYPES_WITH_EXPIRY.includes(
              watchRequiredDocsType || "",
            ) && (
              <CustomInput
                label="Expiry Date"
                type="date"
                placeholder="Enter Expiry Date"
                register={register}
                name="requiredDocsExpiresAt"
                validation={{
                  validate: (value) => {
                    const docType = getValues("requiredDocsType") || "";
                    if (DOCUMENT_TYPES_WITH_EXPIRY.includes(docType)) {
                      if (!value) {
                        return "Expiry date is required for this document type";
                      }
                      if (typeof value === "string" && value.trim()) {
                        if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                          return "Please enter a valid date";
                        }
                        const selected = new Date(value);
                        if (isNaN(selected.getTime())) {
                          return "Please enter a valid date";
                        }
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (selected < today) {
                          return "Expiry date cannot be in the past";
                        }
                      }
                    }
                    return true;
                  },
                }}
                error={errors.requiredDocsExpiresAt}
                disabled={isLoading}
                icon={<CalendarIcon className="w-5 h-5 text-slate-400" />}
              />
            )}

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Upload Document
              </label>
              <div className="border-2 border-dashed border-slate-600 rounded-lg p-3 text-center">
                {requiredDocPreview ? (
                  <div className="space-y-4">
                    <div className="relative">
                      <img
                        src={requiredDocPreview}
                        alt="Required document preview"
                        className="max-w-full h-24 object-contain mx-auto rounded-lg"
                      />
                      {isRequiredDocFromScanner && (
                        <div className="absolute top-0 right-0 bg-green-600 text-white text-xs px-2 py-1 rounded-bl-lg rounded-tr-lg">
                          📱 Scanned
                        </div>
                      )}
                    </div>
                    <div className="flex gap-4 justify-center">
                      {!isEditMode && (
                        <button
                          type="button"
                          onClick={() => {
                            setRequiredDocPreview(null);
                            setValue("requiredDocs", undefined);
                            setIsRequiredDocFromScanner(false);
                          }}
                          className="text-red-400 hover:text-red-300"
                        >
                          Remove Document
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          document
                            .getElementById("required-document-upload")
                            ?.click()
                        }
                        className="inline-flex items-center gap-2 px-3 py-2 text-white rounded-lg hover:bg-slate-600 transition-colors border-2 border-slate-500"
                      >
                        <Upload className="h-4 w-4" />
                        Replace
                      </button>
                      <button
                        type="button"
                        onClick={handleRequiredDocumentUpload}
                        className="inline-flex items-center gap-2 px-3 py-2 text-white rounded-lg hover:bg-slate-600 transition-colors border-2 border-slate-500"
                      >
                        <Scan className="h-4 w-4" />
                        Scan
                      </button>
                    </div>
                    <input
                      id="required-document-upload"
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleRequiredDocUpload}
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <AttachmentIcon className="h-8 w-8 text-slate-400 mx-auto" />
                    <div>
                      <p className="text-slate-400 text-sm mb-2">
                        Drag and Drop the Document or Click to Upload
                      </p>
                      <input
                        id="required-document-upload"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={handleRequiredDocUpload}
                        className="hidden"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            document
                              .getElementById("required-document-upload")
                              ?.click()
                          }
                          className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:bg-slate-600 transition-colors border-2 border-slate-500"
                        >
                          <Upload className="h-4 w-4" />
                          Upload Document
                        </button>
                        <button
                          type="button"
                          onClick={handleRequiredDocumentUpload}
                          className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:bg-slate-600 transition-colors border-2 border-slate-500"
                        >
                          <Scan className="h-4 w-4" />
                          Scan Document
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Optional Document Section */}
        <div className="border border-slate-600 rounded-lg p-3">
          <h3 className="text-base font-medium text-white mb-3">
            Optional Document
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Document Type
              </label>
              <div className="relative flex items-center bg-slate-700 rounded-lg border border-slate-600 focus-within:ring-2 focus-within:ring-blue-500">
                <span className="absolute left-3 flex items-center pointer-events-none">
                  <PassportIcon className="w-5 h-5 text-slate-400" />
                </span>
                <select
                  {...register("optionalDocsType", {
                    onChange: () => {
                      setOptionalDocError("");
                    },
                  })}
                  className="w-full pl-10 pr-8 py-3 bg-slate-700 text-white rounded-lg focus:outline-none appearance-none"
                  disabled={isLoading}
                >
                  <option value="">Select Document Type (Optional)</option>
                  {DOCUMENT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <span className="absolute right-3 pointer-events-none flex items-center">
                  <ArrowDown className="w-6 h-6 text-slate-400" />
                </span>
              </div>
            </div>

            <CustomInput
              label="Document Number (Optional)"
              type="text"
              placeholder="Enter Document Number (Optional)"
              register={register}
              name="optionalDocsNumber"
              validation={{
                onChange: () => {
                  setOptionalDocError("");
                },
                validate: (value) => {
                  if (
                    value &&
                    typeof value === "string" &&
                    value.trim().length === 0
                  ) {
                    return "Document number cannot be empty or spaces only";
                  }
                  return true;
                },
              }}
              error={errors.optionalDocsNumber}
              disabled={isLoading}
              icon={<PassportIcon className="w-5 h-5 text-slate-400" />}
            />

            {DOCUMENT_TYPES_WITH_EXPIRY.includes(
              watchOptionalDocsType || "",
            ) && (
              <CustomInput
                label="Expiry Date"
                type="date"
                placeholder="Enter Expiry Date (Optional)"
                register={register}
                name="optionalDocsExpiresAt"
                validation={{
                  validate: (value) => {
                    const docType = getValues("optionalDocsType") || "";
                    if (
                      docType &&
                      DOCUMENT_TYPES_WITH_EXPIRY.includes(docType)
                    ) {
                      if (!value) {
                        return "Expiry date is required for this document type";
                      }
                      if (typeof value === "string" && value.trim()) {
                        if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                          return "Please enter a valid date";
                        }
                        const selected = new Date(value);
                        if (isNaN(selected.getTime())) {
                          return "Please enter a valid date";
                        }
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (selected < today) {
                          return "Expiry date cannot be in the past";
                        }
                      }
                    }
                    return true;
                  },
                }}
                error={errors.optionalDocsExpiresAt}
                disabled={isLoading}
                icon={<CalendarIcon className="w-5 h-5 text-slate-400" />}
              />
            )}

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Upload Document (Optional)
              </label>
              <div className="border-2 border-dashed border-slate-600 rounded-lg p-3 text-center">
                {optionalDocPreview ? (
                  <div className="space-y-4">
                    <img
                      src={optionalDocPreview}
                      alt="Optional document preview"
                      className="max-w-full h-24 object-contain mx-auto rounded-lg"
                    />
                    <div className="flex gap-4 justify-center">
                      {!isEditMode && (
                        <button
                          type="button"
                          onClick={() => {
                            setOptionalDocPreview(null);
                            setValue("optionalDocs", undefined);
                            setOptionalDocError("");
                          }}
                          className="text-red-400 hover:text-red-300"
                        >
                          Remove Document
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          document
                            .getElementById("optional-document-upload")
                            ?.click()
                        }
                        className="inline-flex items-center gap-2 px-3 py-2 text-white rounded-lg hover:bg-slate-600 transition-colors border-2 border-slate-500"
                      >
                        <Upload className="h-4 w-4" />
                        Replace
                      </button>
                      <button
                        type="button"
                        onClick={handleOptionalDocumentUpload}
                        className="inline-flex items-center gap-2 px-3 py-2 text-white rounded-lg hover:bg-slate-600 transition-colors border-2 border-slate-500"
                      >
                        <Scan className="h-4 w-4" />
                        Scan
                      </button>
                    </div>
                    <input
                      id="optional-document-upload"
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleOptionalDocUpload}
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <AttachmentIcon className="h-8 w-8 text-slate-400 mx-auto" />
                    <div>
                      <p className="text-slate-400 text-sm mb-2">
                        Drag and Drop the Document or Click to Upload (Optional)
                      </p>
                      <input
                        id="optional-document-upload"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={handleOptionalDocUpload}
                        className="hidden"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            document
                              .getElementById("optional-document-upload")
                              ?.click()
                          }
                          className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:bg-slate-600 transition-colors border-2 border-slate-500"
                        >
                          <Upload className="h-4 w-4" />
                          Upload Document
                        </button>
                        <button
                          type="button"
                          onClick={handleOptionalDocumentUpload}
                          className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:bg-slate-600 transition-colors border-2 border-slate-500"
                        >
                          <Scan className="h-4 w-4" />
                          Scan Document
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* Show optional document error */}
              {optionalDocError && (
                <p className="mt-2 text-sm text-red-400">{optionalDocError}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-left mb-6">
        <p className="text-slate-400">
          Add a profile photo (optional). You can skip this step if you prefer.
        </p>
      </div>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Profile Photo (Optional)
          </label>
          <div className="bg-slate-700 rounded-xl p-6 flex flex-col items-center">
            <div
              className="w-full max-w-xl aspect-video border-2 border-blue-500 rounded-lg flex items-center justify-center mb-6 relative"
              style={{ minHeight: 300 }}
            >
              {cameraActive ? (
                <video
                  ref={videoRef}
                  width={400}
                  height={300}
                  className="rounded-lg"
                />
              ) : photoDataUrl ? (
                <img
                  src={photoDataUrl}
                  alt="Captured"
                  className="rounded-lg object-cover w-full h-full"
                />
              ) : isEditMode && customer?.photo ? (
                <img
                  src={`${process.env.REACT_APP_API_BASE_URL}/${customer.photo}`}
                  alt={customer.name}
                  className="rounded-lg object-cover w-full h-full"
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    target.style.display = "none";
                  }}
                />
              ) : (
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <CameraIcon className="h-10 w-10 text-slate-400 mb-2" />
                  <span className="text-slate-400">
                    Live camera feed will appear here
                  </span>
                </div>
              )}
              <canvas
                ref={canvasRef}
                width={400}
                height={300}
                className="hidden"
              />
            </div>
            <div className="flex flex-col items-center">
              <div className="flex gap-4">
                {!cameraActive && !photoDataUrl && (
                  <>
                    <button
                      type="button"
                      onClick={startCamera}
                      className="inline-flex items-center gap-2 px-6 py-2 text-white rounded-lg border-2 border-slate-500 hover:bg-slate-600 transition-colors mb-2"
                    >
                      <CameraIcon className="h-4 w-4" />
                      Take Photo
                    </button>
                    <input
                      id="photo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        document.getElementById("photo-upload")?.click()
                      }
                      className="inline-flex items-center gap-2 px-6 py-2 text-white rounded-lg border-2 border-slate-500 hover:bg-slate-600 transition-colors mb-2"
                    >
                      <Upload className="h-4 w-4" />
                      Upload Photo
                    </button>
                  </>
                )}
              </div>
              {cameraActive && (
                <button
                  type="button"
                  onClick={handleTakePhoto}
                  className="inline-flex items-center gap-2 px-6 py-2 text-white rounded-lg border-2 border-slate-500 hover:bg-slate-600 transition-colors mb-2"
                >
                  <CameraIcon className="h-4 w-4" />
                  Capture
                </button>
              )}
              {photoDataUrl && (
                <button
                  type="button"
                  onClick={() => {
                    setPhotoDataUrl(null);
                    setPhotoPreview(null);
                    setValue("photo", undefined);
                  }}
                  className="text-red-400 hover:text-red-300"
                >
                  Remove Photo
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return renderStep0();
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return renderStep0();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 shadow-2xl border border-slate-600 rounded-lg w-full max-w-5xl max-h-[85vh] flex flex-col backdrop-blur-sm">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-slate-700 flex-shrink-0">
          <h2 className="text-lg font-semibold text-yellow-400">{formTitle}</h2>
          <button
            onClick={handleCancelClick}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            disabled={isLoading}
          >
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>

        {/* Step Indicator - Hide for step 0 (scanner) */}
        {currentStep > 0 && (
          <div className="flex-shrink-0 py-2">{renderStepIndicator()}</div>
        )}

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-4 py-2">
          <form
            onSubmit={
              currentStep === 3 ? handleStep3Submit : handleSubmit(onFormSubmit)
            }
            id="customer-form"
          >
            {renderCurrentStep()}
          </form>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between p-3 border-t border-slate-700 flex-shrink-0">
          <button
            type="button"
            onClick={
              currentStep === 0 || currentStep === 1
                ? handleCancelClick
                : handlePreviousStep
            }
            className="flex items-center gap-2 px-4 py-2 text-slate-400 hover:text-white transition-colors"
            disabled={
              isLoading ||
              updateCustomerMutation.isPending ||
              updateDocumentMutation.isPending ||
              updatePhotoMutation.isPending
            }
          >
            {currentStep > 1 && <ArrowLeft className="h-4 w-4" />}
            {currentStep === 0 || currentStep === 1 ? "Cancel" : "Previous"}
          </button>

          {currentStep > 0 && currentStep < 3 ? (
            <button
              type="button"
              onClick={handleNextStep}
              disabled={
                isLoading ||
                updateCustomerMutation.isPending ||
                updateDocumentMutation.isPending
              }
              className="flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-800 transition-colors font-medium"
            >
              {updateCustomerMutation.isPending ||
              updateDocumentMutation.isPending
                ? "Updating..."
                : isEditMode
                  ? "Update and Next"
                  : "Next"}
              <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <PrimaryButton
              type="submit"
              form="customer-form"
              disabled={isLoading || updatePhotoMutation.isPending}
              className="flex items-center gap-2"
            >
              {updatePhotoMutation.isPending
                ? "Updating..."
                : isEditMode
                  ? "Update & Complete"
                  : "Save and Complete"}
            </PrimaryButton>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={showCancelDialog}
        title="Confirm Cancellation"
        description="Would you like to cancel this process?"
        icon={<Trash2 className="h-8 w-8 text-white" />}
        iconBgColor="bg-red-600"
        confirmColor="bg-red-600 hover:bg-red-700"
        confirmText="Yes"
        cancelText="Cancel"
        onCancel={handleCancelDialog}
        onConfirm={handleConfirmCancel}
      />

      {/* Document Camera Modal */}
      {documentCameraActive && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
          <div className="bg-slate-800 rounded-lg w-full max-w-3xl">
            <div className="p-4 border-b border-slate-700">
              <h3 className="text-lg font-semibold text-white">
                Scan {scanningDocument === "required" ? "Required" : "Optional"}{" "}
                Document
              </h3>
            </div>
            <div className="p-4">
              <div
                className="relative bg-black rounded-lg overflow-hidden mb-4"
                style={{ minHeight: 400 }}
              >
                {capturedDocumentPreview ? (
                  <div className="w-full h-full flex flex-col items-center justify-center p-4">
                    <img
                      src={capturedDocumentPreview}
                      alt="Captured document"
                      className="max-w-full max-h-[400px] object-contain rounded-lg mb-4"
                    />
                    <p className="text-white mb-4">
                      Review your captured document
                    </p>
                  </div>
                ) : (
                  <>
                    <video
                      ref={documentVideoRef}
                      className="w-full h-full object-contain"
                      autoPlay
                      playsInline
                    />
                    <canvas ref={documentCanvasRef} className="hidden" />

                    {/* Camera Controls Overlay - Only show if camera has capabilities */}
                    {(supportsZoom || supportsFocus) && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                        <div className="flex items-center justify-between">
                          {/* Zoom Controls - Only show if zoom is supported */}
                          {supportsZoom && (
                            <div className="bg-black/50 backdrop-blur rounded-lg p-3">
                              <div className="flex items-center gap-3">
                                <svg
                                  className="w-5 h-5 text-white"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                                  />
                                </svg>
                                <input
                                  type="range"
                                  min="1"
                                  max="5"
                                  step="0.1"
                                  value={cameraZoom}
                                  onChange={(e) =>
                                    handleZoomChange(parseFloat(e.target.value))
                                  }
                                  className="w-32 accent-blue-500"
                                  style={{
                                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((cameraZoom - 1) / 4) * 100}%, #475569 ${((cameraZoom - 1) / 4) * 100}%, #475569 100%)`,
                                  }}
                                />
                                <span className="text-white font-medium min-w-[3rem]">
                                  {cameraZoom.toFixed(1)}x
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Focus Button - Only show if focus is supported */}
                          {supportsFocus && (
                            <button
                              onClick={handleFocus}
                              className="bg-black/50 backdrop-blur hover:bg-black/70 text-white rounded-lg px-4 py-3 text-sm font-medium flex items-center gap-2 transition-all ml-auto"
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <circle cx="12" cy="12" r="3" strokeWidth="2" />
                                <path
                                  d="M12 1v6m0 6v6m6-12h6M6 12H1"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                />
                              </svg>
                              Focus
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Instructions - Always show */}
                    <div className="absolute bottom-4 left-0 right-0 text-center">
                      <p className="text-white bg-black/50 inline-block px-3 py-1 rounded-lg text-sm">
                        Position document clearly in view • Hold steady for best
                        quality
                      </p>
                    </div>
                  </>
                )}
              </div>
              <div className="flex gap-4 justify-center">
                {capturedDocumentPreview ? (
                  <>
                    <button
                      onClick={proceedWithCapturedDocument}
                      className="px-6 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium flex items-center gap-2"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Proceed
                    </button>
                    <button
                      onClick={retakeDocument}
                      className="px-6 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg font-medium flex items-center gap-2"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      Retake
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={captureDocument}
                      className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium flex items-center gap-2"
                    >
                      <Camera className="h-5 w-5" />
                      Capture
                    </button>
                    <button
                      onClick={stopDocumentCamera}
                      className="px-6 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg font-medium flex items-center gap-2"
                    >
                      <X className="h-5 w-5" />
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
